/**
 * Extracts a human-readable error message from various error formats
 *
 * Supports:
 * - RTK Query errors (data.message, data.error, data.title)
 * - Standard Error objects (error.message)
 * - String errors
 * - Unknown error types (returns generic message)
 * - 500-level errors (returns generic message to avoid exposing server details)
 *
 * @param error - The error object to parse
 * @returns A user-friendly error message string
 */
export const getErrorMessage = (error: unknown): string => {
  if (!error) return '';

  // RTK Query error format
  if (typeof error === 'object' && error !== null) {
    // Check for 500-level errors and return generic message
    if ('status' in error && typeof error.status === 'number' && error.status >= 500) {
      return 'An unexpected error occurred. Please try again later.';
    }

    if ('data' in error && error.data) {
      const data = error.data as Record<string, unknown>;
      if (typeof data === 'string') return data;
      if (data.message && typeof data.message === 'string') return data.message;
      if (data.error && typeof data.error === 'string') return data.error;
      if (data.title && typeof data.title === 'string') return data.title;
      if (data.errors && Array.isArray(data.errors) && !!data.errors[0]) {
        const singleError = data.errors[0];
        if (singleError.message) return singleError.message;
      }
      if (data.error && Array.isArray(data.error) && !!data.error[0]) {
        const singleError = data.error[0];
        if (singleError.message) return singleError.message;
      }
    }
    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }
    if ('error' in error && typeof error.error === 'string') {
      return error.error;
    }
  }

  if (typeof error === 'string') return error;

  return 'An unexpected error occurred. Please try again.';
};
